import { Fragment, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import { linkClick } from './routerLink';

export interface NavItem {
    to: string;
    /** i18n id for the label. Omit when passing a ready-made `label`. */
    labelId?: string;
    /**
     * A label that isn't a translatable constant — used for items named after a
     * record (e.g. "Planum 2"). Takes precedence over `labelId`.
     */
    label?: ReactNode;
    icon: ReactNode;
    /** Whether the current path counts as this item being active. */
    isActive: (pathname: string) => boolean;
    /** Indented sub-items, shown only while this item is contextually active. */
    children?: NavItem[];
    /** Muted caption above the sub-items naming the context (the open record). */
    childrenHeader?: ReactNode;
}

/** An item's label, whether it's a translation id or a data-driven node. */
function navItemLabel(item: NavItem): ReactNode {
    if (item.label !== undefined) {
        return item.label;
    }
    if (item.labelId) {
        return <FormattedMessage id={item.labelId} />;
    }
    return null;
}

interface NavListProps {
    /** One inner array per section; sections are divider-separated. */
    groups: NavItem[][];
    pathname: string;
    onNavigate: (to: string) => void;
}

/**
 * The shared drawer menu, rendered from a config of divider-separated sections.
 * Used by both the office Layout and the field FieldLayout so the two menus
 * stay visually identical; each owns its own config. A section whose items are
 * all permission-gated away collapses to empty and is dropped.
 */
export default function NavList({
    groups,
    pathname,
    onNavigate
}: NavListProps) {
    const visibleGroups = groups.filter((group) => group.length > 0);

    function renderChild(child: NavItem) {
        const active = child.isActive(pathname);
        return (
            <ListItem key={child.to} disablePadding>
                <ListItemButton
                    component={RouterLink}
                    to={child.to}
                    selected={active}
                    onClick={linkClick(() => onNavigate(child.to))}
                    sx={{
                        pl: 2,
                        ml: '-2px',
                        borderLeft: 2,
                        borderColor: 'transparent',
                        '&.Mui-selected': {
                            borderColor: 'primary.main',
                            bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.12),
                            '&:hover': {
                                bgcolor: (theme) =>
                                    alpha(theme.palette.primary.main, 0.18)
                            }
                        }
                    }}
                >
                    <ListItemIcon
                        sx={{ color: active ? 'primary.main' : undefined }}
                    >
                        {child.icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={navItemLabel(child)}
                        slotProps={{
                            primary: {
                                color: active ? 'primary.main' : undefined,
                                sx: { fontWeight: active ? 600 : 400 }
                            }
                        }}
                    />
                </ListItemButton>
            </ListItem>
        );
    }

    function renderItem(item: NavItem) {
        const active = item.isActive(pathname);
        return (
            <Box key={item.to}>
                <ListItem disablePadding>
                    <ListItemButton
                        component={RouterLink}
                        to={item.to}
                        selected={active}
                        onClick={linkClick(() => onNavigate(item.to))}
                    >
                        <ListItemIcon
                            sx={{ color: active ? 'primary.main' : undefined }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={navItemLabel(item)}
                            slotProps={{
                                primary: {
                                    sx: { fontWeight: active ? 600 : 400 }
                                }
                            }}
                        />
                    </ListItemButton>
                </ListItem>
                {item.children && (
                    <Box
                        sx={{ ml: 2.5, borderLeft: 2, borderColor: 'divider' }}
                    >
                        {item.childrenHeader && (
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    px: 2,
                                    py: 0.75,
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    fontWeight: 600,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {item.childrenHeader}
                            </Typography>
                        )}
                        {item.children.map((child) => renderChild(child))}
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <List>
            {visibleGroups.map((group, groupIndex) => (
                <Fragment key={groupIndex}>
                    {groupIndex > 0 && <Divider sx={{ my: 1 }} />}
                    {group.map((item) => renderItem(item))}
                </Fragment>
            ))}
        </List>
    );
}
